import {
  type IAcceptedImportProposal,
  ImportProposalAcceptance,
  ImportProposalMatching,
} from '@principle-theorem/principle-core';
import {
  type IAIReceptionistKnowledgeBase,
  type IAIReceptionistSectionQaPairs,
  type IImportProposal,
  type IImportProposalFields,
  IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS,
} from '@principle-theorem/principle-core/interfaces';
import { isBoolean, isNumber, isString } from 'lodash';
import {
  AI_KNOWLEDGE_BASE_QA_CATEGORY_LABELS,
  AI_KNOWLEDGE_BASE_SECTION_LABELS,
  AiKnowledgeBaseSection,
} from '../ai-receptionist-knowledge-base-section';

export enum ImportReviewTargetKind {
  Field = 'field',
  QaPair = 'qaPair',
  CustomSection = 'customSection',
}

export type ImportReviewTarget =
  | { kind: ImportReviewTargetKind.Field; field: keyof IImportProposalFields }
  | {
      kind: ImportReviewTargetKind.QaPair;
      key: keyof IAIReceptionistSectionQaPairs;
      index: number;
    }
  | { kind: ImportReviewTargetKind.CustomSection; index: number };

/**
 * A value as the review shows it: a Q&A pair or custom section has a
 * heading (question or title) and a body, a field has only the body.
 */
export interface IImportReviewValue {
  heading?: string;
  body: string;
}

export interface IImportReviewItem {
  id: string;
  label: string;
  categoryLabel?: string;
  ariaLabel: string;
  current?: IImportReviewValue;
  proposed: IImportReviewValue;
  provenance: string;
  /**
   * Set when the suggestion updates an item the practice already has: the
   * row shows an Update tag and this copy says what accepting it keeps.
   */
  updateNote?: string;
  selected: boolean;
}

export interface IImportReviewGroup {
  sectionLabel: string;
  items: IImportReviewItem[];
}

export interface IImportReviewEntry
  extends Omit<IImportReviewItem, 'selected'> {
  sectionLabel: string;
  target: ImportReviewTarget;
  initiallySelected: boolean;
}

const fieldLabels: Record<keyof IImportProposalFields, string> = {
  practiceDescription: 'Practice Description',
  parkingAccess: 'Parking & Access',
  ourTeam: 'Our Team',
  appointmentsBooking: 'Appointments Booking',
  cancellationPolicy: 'Cancellation Policy',
  allowsCancellations: 'Allow Cancellations',
  cancellationFeeWindowHours: 'Cancellation Fee Window (Hours)',
  reschedulingPolicy: 'Rescheduling Policy',
  allowsRescheduling: 'Allow Rescheduling',
  reschedulingWindowHours: 'Rescheduling Window (Hours)',
  additionalInfo: 'Additional Information',
};
// Proposal fields grouped by the editor section that edits them. Sections
// are iterated in AiKnowledgeBaseSection order so the review mirrors the
// editor.
const sectionFields: Partial<
  Record<AiKnowledgeBaseSection, (keyof IImportProposalFields)[]>
> = {
  [AiKnowledgeBaseSection.PracticeDetails]: [
    'practiceDescription',
    'parkingAccess',
  ],
  [AiKnowledgeBaseSection.OurTeam]: ['ourTeam'],
  [AiKnowledgeBaseSection.AppointmentsPolicies]: [
    'appointmentsBooking',
    'cancellationPolicy',
    'allowsCancellations',
    'cancellationFeeWindowHours',
    'reschedulingPolicy',
    'allowsRescheduling',
    'reschedulingWindowHours',
  ],
  [AiKnowledgeBaseSection.EmergencyAdditional]: ['additionalInfo'],
};
const qaUpdateNote =
  'Replaces the current answer. The question is kept as it is.';
const customSectionUpdateNote =
  'Replaces the current content. The title and its questions are kept as they are.';
const textFieldUpdateNote = 'Replaces the current text.';
const settingFieldUpdateNote = 'Replaces the current setting.';

/**
 * Turns an Import Proposal into the rows the import review shows, grouped by
 * the editor section that edits them, and turns the rows the practice ticked
 * back into what it accepted.
 */
export class ImportReview {
  static buildEntries(
    proposal: IImportProposal,
    knowledgeBase: IAIReceptionistKnowledgeBase
  ): IImportReviewEntry[] {
    return Object.values(AiKnowledgeBaseSection).flatMap((section) => {
      const sectionLabel = AI_KNOWLEDGE_BASE_SECTION_LABELS[section];
      switch (section) {
        case AiKnowledgeBaseSection.CommonQuestions:
          return ImportReview._buildQaEntries(
            proposal,
            knowledgeBase,
            sectionLabel
          );
        case AiKnowledgeBaseSection.CustomSections:
          return ImportReview._buildCustomSectionEntries(
            proposal,
            knowledgeBase,
            sectionLabel
          );
        default:
          return ImportReview._buildFieldEntries(
            proposal,
            knowledgeBase,
            section,
            sectionLabel
          );
      }
    });
  }

  static initialSelection(entries: IImportReviewEntry[]): Set<string> {
    return new Set(
      entries.filter((entry) => entry.initiallySelected).map(({ id }) => id)
    );
  }

  static groupEntries(
    entries: IImportReviewEntry[],
    selected: Set<string>
  ): IImportReviewGroup[] {
    const groups = new Map<string, IImportReviewItem[]>();
    for (const entry of entries) {
      const items = groups.get(entry.sectionLabel) ?? [];
      items.push({
        id: entry.id,
        label: entry.label,
        categoryLabel: entry.categoryLabel,
        ariaLabel: entry.ariaLabel,
        current: entry.current,
        proposed: entry.proposed,
        provenance: entry.provenance,
        updateNote: entry.updateNote,
        selected: selected.has(entry.id),
      });
      groups.set(entry.sectionLabel, items);
    }
    return Array.from(groups, ([sectionLabel, items]) => ({
      sectionLabel,
      items,
    }));
  }

  static toAccepted(
    entries: IImportReviewEntry[],
    selected: Set<string>
  ): IAcceptedImportProposal {
    const accepted: IAcceptedImportProposal = {
      fields: {},
      sectionQaPairs: {},
      customSections: [],
    };
    for (const entry of entries) {
      if (!selected.has(entry.id)) {
        continue;
      }
      const target = entry.target;
      switch (target.kind) {
        case ImportReviewTargetKind.Field:
          accepted.fields[target.field] = true;
          break;
        case ImportReviewTargetKind.QaPair:
          accepted.sectionQaPairs[target.key] = [
            ...(accepted.sectionQaPairs[target.key] ?? []),
            target.index,
          ];
          break;
        case ImportReviewTargetKind.CustomSection:
          accepted.customSections.push(target.index);
          break;
        default:
          break;
      }
    }
    return accepted;
  }

  static heading(proposal: IImportProposal): string {
    const host = ImportReview._host(proposal.rootUrl);
    return `Read ${proposal.pagesRead} of ${proposal.pagesRequested} pages from ${host}`;
  }

  private static _host(rootUrl: string): string {
    try {
      return new URL(rootUrl).host;
    } catch {
      return rootUrl;
    }
  }

  /**
   * Renders a Knowledge Base or proposed value as review text. A blank or
   * absent value renders as nothing, so the review shows "Empty" for it.
   */
  private static _formatFieldValue(value: unknown): string | undefined {
    if (isString(value)) {
      const trimmed = value.trim();
      return trimmed === '' ? undefined : trimmed;
    }
    if (isBoolean(value)) {
      return value ? 'Yes' : 'No';
    }
    if (isNumber(value)) {
      return `${value} hours`;
    }
    return;
  }

  private static _fieldUpdateNote(proposedValue: unknown): string {
    return isString(proposedValue)
      ? textFieldUpdateNote
      : settingFieldUpdateNote;
  }

  private static _buildFieldEntries(
    proposal: IImportProposal,
    knowledgeBase: IAIReceptionistKnowledgeBase,
    section: AiKnowledgeBaseSection,
    sectionLabel: string
  ): IImportReviewEntry[] {
    return (sectionFields[section] ?? []).flatMap((field) => {
      const proposed = proposal.fields[field];
      if (!proposed) {
        return [];
      }
      const label = fieldLabels[field];
      const current = ImportReview._formatFieldValue(knowledgeBase[field]);
      const proposedBody = ImportReview._formatFieldValue(proposed.value) ?? '';
      // The import is never sent the fields' current values, so a suggestion
      // that only repeats one is left out here, as it would change nothing
      if (
        current !== undefined &&
        ImportProposalMatching.isSameText(current, proposedBody)
      ) {
        return [];
      }
      const isEmpty = ImportProposalAcceptance.isFieldEmpty(
        knowledgeBase,
        field
      );
      // A filled field is replaced on accept, so it is shown as an update
      // exactly like a referenced Q&A pair or custom section
      const entry: IImportReviewEntry = {
        id: `field:${field}`,
        label,
        sectionLabel,
        ariaLabel: isEmpty
          ? `Use proposed ${label} in ${sectionLabel}`
          : `Update the ${label} in ${sectionLabel}`,
        current: current ? { body: current } : undefined,
        proposed: { body: proposedBody },
        provenance: proposed.provenance,
        updateNote: isEmpty
          ? undefined
          : ImportReview._fieldUpdateNote(proposed.value),
        target: { kind: ImportReviewTargetKind.Field, field },
        initiallySelected: isEmpty,
      };
      return [entry];
    });
  }

  private static _buildQaEntries(
    proposal: IImportProposal,
    knowledgeBase: IAIReceptionistKnowledgeBase,
    sectionLabel: string
  ): IImportReviewEntry[] {
    return IMPORT_PROPOSAL_SECTION_QA_PAIR_KEYS.flatMap((key) =>
      (proposal.sectionQaPairs[key] ?? []).flatMap((pair, index) => {
        const existingPairs = knowledgeBase.sectionQaPairs?.[key];
        const categoryLabel = AI_KNOWLEDGE_BASE_QA_CATEGORY_LABELS[key];
        const target: ImportReviewTarget = {
          kind: ImportReviewTargetKind.QaPair,
          key,
          index,
        };
        const updating = pair.existingId
          ? existingPairs?.find((candidate) => candidate.id === pair.existingId)
          : undefined;
        if (updating) {
          const entry: IImportReviewEntry = {
            id: `qaPair:${key}:${index}`,
            label: updating.question,
            categoryLabel,
            sectionLabel,
            ariaLabel: `Update the answer to ${updating.question} in ${sectionLabel}`,
            current: { heading: updating.question, body: updating.answer },
            proposed: { heading: updating.question, body: pair.answer },
            provenance: pair.provenance,
            updateNote: qaUpdateNote,
            target,
            initiallySelected: false,
          };
          return [entry];
        }
        const entry: IImportReviewEntry = {
          id: `qaPair:${key}:${index}`,
          label: pair.question,
          categoryLabel,
          sectionLabel,
          ariaLabel: `Use proposed ${pair.question} in ${sectionLabel}`,
          proposed: { heading: pair.question, body: pair.answer },
          provenance: pair.provenance,
          target,
          initiallySelected: true,
        };
        return [entry];
      })
    );
  }

  private static _buildCustomSectionEntries(
    proposal: IImportProposal,
    knowledgeBase: IAIReceptionistKnowledgeBase,
    sectionLabel: string
  ): IImportReviewEntry[] {
    const existingSections = knowledgeBase.customSections;
    return proposal.customSections.flatMap((customSection, index) => {
      const target: ImportReviewTarget = {
        kind: ImportReviewTargetKind.CustomSection,
        index,
      };
      const updating = isNumber(customSection.existingIndex)
        ? existingSections?.[customSection.existingIndex]
        : undefined;
      if (updating) {
        const entry: IImportReviewEntry = {
          id: `customSection:${index}`,
          label: updating.title,
          sectionLabel,
          ariaLabel: `Update the content of ${updating.title} in ${sectionLabel}`,
          current: { heading: updating.title, body: updating.content },
          proposed: { heading: updating.title, body: customSection.content },
          provenance: customSection.provenance,
          updateNote: customSectionUpdateNote,
          target,
          initiallySelected: false,
        };
        return [entry];
      }
      const entry: IImportReviewEntry = {
        id: `customSection:${index}`,
        label: customSection.title,
        sectionLabel,
        ariaLabel: `Use proposed ${customSection.title} in ${sectionLabel}`,
        proposed: { heading: customSection.title, body: customSection.content },
        provenance: customSection.provenance,
        target,
        initiallySelected: true,
      };
      return [entry];
    });
  }
}
